import { useCallback } from 'react';
import useSWR from 'swr';
import { AmplifyGet } from '@sunrun/sales-experience-shared/amplify-api-wrapper';
import type {
  UserHierarchyApiResponse,
  UserHierarchyNode,
} from 'types/userHierarchy';
import { logger } from 'providers/logger';

const AMPLIFY_API = 'SalesExpDashboardApi';

type UseUserHierarchyArgs = {
  jwt: string | null | undefined;
  userEmailForHierarchy: string | null;
  enabled?: boolean;
};

type UseUserHierarchyResult = {
  hierarchy: UserHierarchyNode | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  hasDirectReports: boolean;
};

type HierarchyFetcherKey = [
  path: string,
  jwt: string,
  userEmailForHierarchy: string,
];

const getHierarchyErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unable to load hierarchy. Please try again.';
};

const hierarchyFetcher = async ([
  ,
  jwt,
  userEmailForHierarchy,
]: HierarchyFetcherKey): Promise<UserHierarchyApiResponse> => {
  return AmplifyGet<UserHierarchyApiResponse>(
    'Get User Hierarchy',
    AMPLIFY_API,
    '/userHierarchy',
    {
      queryStringParameters: {
        jwt,
        user_email: userEmailForHierarchy,
      },
    },
    logger,
  );
};

const useUserHierarchy = ({
  jwt,
  userEmailForHierarchy,
  enabled = true,
}: UseUserHierarchyArgs): UseUserHierarchyResult => {
  const swrKey: HierarchyFetcherKey | null =
    enabled && jwt && userEmailForHierarchy
      ? ['/userHierarchy', jwt, userEmailForHierarchy]
      : null;

  const { data, error, isLoading, mutate } = useSWR(swrKey, hierarchyFetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const refetch = useCallback(() => {
    void mutate();
  }, [mutate]);

  const hierarchy = swrKey ? data?.hierarchy ?? null : null;
  const loading = swrKey ? isLoading : false;
  const errorMessage = swrKey && error ? getHierarchyErrorMessage(error) : null;

  const hasDirectReports =
    hierarchy != null &&
    Array.isArray(hierarchy.Reports) &&
    hierarchy.Reports.length > 0;

  return {
    hierarchy,
    loading,
    error: errorMessage,
    refetch,
    hasDirectReports,
  };
};

export { useUserHierarchy, type UseUserHierarchyResult };
